import { Login } from '@platform/client';
import { Button } from '@platform/components';
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { SetPasswordInput } from '../components/SetPasswordInput';
import { UnauthedPageWithNavbar } from '../../layout/UnauthedPageWithNavbar';

type FormValues = {
  password: string;
  passwordConfirmation: string;
};

export const UpdatePasswordPage = () => {
  const [token, setToken] = useState('');
  const [passwordReset, setPasswordReset] = useState(false);

  useEffect(() => {
    //Done in useEffect due to ssr

    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    setToken(token || '');
  }, []);

  return (
    <UnauthedPageWithNavbar>
      <div className='w-full h-[80vh] flex flex-col gap-8 md:justify-center items-center p-4 overflow-y-scroll'>
        {passwordReset && <PasswordResetConfirmationSegment />}
        {!passwordReset && (
          <PasswordUpdateSegment token={token} setPasswordReset={setPasswordReset} />
        )}
      </div>
    </UnauthedPageWithNavbar>
  );
};

type PasswordUpdateSegmentProps = {
  token: string;
  setPasswordReset: (value: boolean) => void;
};

const PasswordUpdateSegment = (props: PasswordUpdateSegmentProps) => {
  const { token, setPasswordReset } = props;
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormValues>({ mode: 'onBlur', reValidateMode: 'onBlur' });

  const resetPasswordMutation = Login.useUpdatePassword({
    mutation: {
      onSuccess: () => {
        setPasswordReset(true);
      },
      onError: (error) => {
        if ((error as AxiosError).status === 401) {
          setPasswordReset(false);
        }
      },
    },
  });

  const submitPasswordUpdate: SubmitHandler<FormValues> = async (data) => {
    resetPasswordMutation.mutate({
      data: {
        newPassword: data.password,
        verificationToken: token,
      },
    });
  };

  const firstPassword = watch('password');
  const secondPassword = watch('passwordConfirmation');

  const passwordsEntered = !!firstPassword && !!secondPassword;
  const passwordsMatch = firstPassword === secondPassword;

  return (
    <>
      <h2 className='text-display-sm md:text-display-md'>Update your password</h2>
      <div className='w-full md:w-[514px] text-lg text-center overflow-wrap'>
        Create a new password, then use it to log in on the next page.
      </div>
      <form
        className='flex flex-col gap-8 w-full md:w-[514px] max-w-[514px] items-center'
        name='login'
        onSubmit={handleSubmit(submitPasswordUpdate)}
      >
        <SetPasswordInput
          password={firstPassword || ''}
          register={register}
          errors={errors.password}
          label={'New password'}
          placeholder='Enter a new password'
        />
        <div className='flex flex-col gap-2 '>
          <SetPasswordInput
            password={secondPassword || ''}
            register={register}
            errors={errors.passwordConfirmation}
            hideRequirements={true}
            registerKey='passwordConfirmation'
            label={'Re-enter password'}
            placeholder='Re-enter your new password'
          />
          {passwordsEntered && !passwordsMatch && (
            <div className='text-sm text-error-500 overflow-wrap'>Passwords must match</div>
          )}
        </div>

        <Button
          isDisabled={!passwordsEntered || !passwordsMatch}
          type='submit'
          variant='primary'
          size='md'
          additionalClassnames='w-[160px]'
        >
          Reset password
        </Button>
      </form>
      <a className='underline text-brand-500' href='mailto:match@movingworlds.org'>
        Email us for help
      </a>
    </>
  );
};

const PasswordResetConfirmationSegment = () => {
  return (
    <>
      <h2 className='text-display-sm md:text-display-md'>
        Success! Your password has been updated
      </h2>
      <div className='w-full md:w-[514px] text-lg text-center overflow-wrap'>
        You can now use it to log in.
      </div>
      <Button href='/login' variant='primary' size='md' additionalClassnames='w-[160px]'>
        Back to log in
      </Button>
    </>
  );
};
